import { Space } from '@billy/design-system'
import { Button, Icon, Menu } from '@shinetools/lumen-react'
import { X } from 'lucide-react'
import { ReactElement, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { OrganizationFeatureFlag } from '@features/settings/enums/organizationFeatureFlag'
import { useOrganizationFeatureFlags } from '@features/settings/hooks/useOrganizationFeatureFlags'
import { TransactionOriginatorTypeGroup } from '@features/transactions/enums/transactionOriginatorTypeGroup'
import { useUserOrganization } from '@modules-deprecated/app/organization'

import { useSegment } from '../../../../../../../../hooks'
import { defaultTransactionsQuery } from '../../../../../../constants/defaultTransactionsQuery'
import { useTransactionsListFilters } from '../../../../../../contexts/transactionsListFiltersContext'
import { useTransactionsActiveFiltersCount } from '../../hooks/useTransactionsActiveFiltersCount'
import { useTransactionsFilterJournalCodes } from '../../hooks/useTransactionsFilterJournalCodes'
import { getTransactionsFilterTypeGroups } from '../../utils/getTransactionsFilterTypeGroups'
import * as Styled from './styles'

interface TransactionsSearchFilterMenuProps {
  isOpen: boolean
  onOpenChange: (isOpen: boolean) => void
}

export const TransactionsSearchFilterMenu = ({
  isOpen,
  onOpenChange,
}: TransactionsSearchFilterMenuProps): ReactElement => {
  const { t } = useTranslation()
  const { track } = useSegment()
  const { organization } = useUserOrganization()
  const { hasFeatureFlag } = useOrganizationFeatureFlags()
  const {
    appliedFilters: { journalCode, originatorTypeGroup, withVoided },
    setFilters,
  } = useTransactionsListFilters()

  const areJournalFiltersEnabled =
    organization?.countryId === 'FR' && hasFeatureFlag(OrganizationFeatureFlag.FrenchJournals)

  const {
    journalCode: defaultJournalCode,
    originatorTypeGroup: defaultOriginatorTypeGroup,
    page: defaultPage,
    withVoided: defaultWithVoided,
  } = defaultTransactionsQuery

  const typeGroups = useMemo(() => getTransactionsFilterTypeGroups(t), [t])
  const journalCodes = useTransactionsFilterJournalCodes({ isDisabled: !areJournalFiltersEnabled })
  const activeFiltersCount = useTransactionsActiveFiltersCount()
  const isClearFiltersDisabled = activeFiltersCount === 0

  const handleTypeToggle = useCallback(
    (type: TransactionOriginatorTypeGroup, checked: boolean) => {
      const currentTypes = originatorTypeGroup ?? []
      const nextTypes = checked ? [...currentTypes, type] : currentTypes.filter((value) => value !== type)

      setFilters({ originatorTypeGroup: nextTypes, page: defaultPage })
    },
    [defaultPage, originatorTypeGroup, setFilters],
  )

  const handleJournalCodeToggle = useCallback(
    (journalCodeValue: string, checked: boolean) => {
      const currentJournalCodes = journalCode ?? journalCodes.map(({ value }) => value)
      const nextJournalCodes = checked
        ? [...currentJournalCodes, journalCodeValue]
        : currentJournalCodes.filter((value) => value !== journalCodeValue)

      const isEverythingSelected = nextJournalCodes.length === journalCodes.length

      setFilters({
        journalCode: isEverythingSelected ? undefined : nextJournalCodes,
        page: defaultPage,
      })
    },
    [defaultPage, journalCode, journalCodes, setFilters],
  )

  const handleVoidedToggle = useCallback(
    (checked: boolean) => {
      track('xxx - Bookkeeping - Ledger', { action: 'Show voided' })
      setFilters({ page: defaultPage, withVoided: checked })
    },
    [defaultPage, setFilters, track],
  )

  const handleClearFiltersClick = useCallback(() => {
    track('xxx - Bookkeeping - Ledger', { action: 'Clear all filters' })
    setFilters({
      journalCode: defaultJournalCode,
      originatorTypeGroup: defaultOriginatorTypeGroup,
      page: defaultPage,
      withVoided: defaultWithVoided,
    })
  }, [defaultJournalCode, defaultOriginatorTypeGroup, defaultPage, defaultWithVoided, setFilters, track])

  return (
    <Menu
      onOpenChange={onOpenChange}
      open={isOpen}
      placement="bottom-end"
      trigger={<Styled.TransactionsSearchFilterMenuAnchor />}
    >
      {areJournalFiltersEnabled ? (
        /* Journals */
        <Menu.Group label={t('transactions.table_actions.journal_codes_selector.label')}>
          {journalCodes.map(({ label, value }) => (
            <Menu.Checkbox
              checked={journalCode ? journalCode.includes(value) : true}
              key={value}
              label={label}
              onCheckedChange={(checked) => handleJournalCodeToggle(value, checked)}
            />
          ))}
        </Menu.Group>
      ) : (
        /* Ledger entry types */
        <Menu.Group label={t('transactions.table_actions.types_selector.label')}>
          {typeGroups.map(({ label, value }) => (
            <Menu.Checkbox
              checked={(originatorTypeGroup ?? []).includes(value)}
              key={value}
              label={label}
              onCheckedChange={(checked) => handleTypeToggle(value, checked)}
            />
          ))}
        </Menu.Group>
      )}

      <Space size="s4" />
      <Menu.Divider />
      <Space size="s6" />

      {/* Show voided */}
      <Menu.Switch
        checked={!!withVoided}
        label={t('transactions.table_actions.show_voided')}
        onCheckedChange={handleVoidedToggle}
      />

      <Space size="s6" />
      <Menu.Divider />
      <Space size="s6" />

      {/* Clear all */}
      <Styled.TransactionsSearchFilterMenuClearButton>
        <Button
          color="primary"
          disabled={isClearFiltersDisabled}
          label={t('transactions.table_actions.clear_all_filters')}
          onClick={handleClearFiltersClick}
          size="small"
          startIcon={<Icon icon={X} />}
          variant="text"
        />
      </Styled.TransactionsSearchFilterMenuClearButton>
      <Space size="s2" />
    </Menu>
  )
}
